import { useState } from 'react'
import { message } from 'antd'
import type {
  BalanceRegistration,
  InviteeInput,
} from '@sportninja/common/services/teamBalance'
import type { InviteConfirmTarget, InviteFormTarget } from '../types/slotInvite'
import { guardMessage } from '../utils/balanceError'
import { balanceText } from '../utils/balanceI18n'
import { inviteeName, isInvitedSlot } from '../utils/balanceRowKind'
import { resolveSlotTarget } from '../utils/resolveSlotTarget'

type Mutation<Variables> = {
  mutateAsync: (variables: Variables) => Promise<unknown>
  isPending: boolean
}

export interface SlotInviteMutations {
  addInvitedSlots: Mutation<{
    team_registration_id: string
    invitees: InviteeInput[]
  }>
  inviteIntoSlot: Mutation<{ slotId: string; invitee: InviteeInput }>
  changeInvitee: Mutation<{ slotId: string; invitee: InviteeInput }>
  cancelInvite: Mutation<{ slotId: string }>
  resendInvite: Mutation<{ slotId: string }>
  removeSlot: Mutation<{ slotId: string }>
}

interface UseSlotInviteFlowsOptions {
  /** The sheet in view: where a row's slot is looked up. */
  registrations: readonly BalanceRegistration[]
  mutations: SlotInviteMutations
}

const isConflict = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 409

const sentToast = (invitees: InviteeInput[]): string =>
  invitees.length === 1
    ? balanceText('common:balanceInviteSentSuccess', {
        name: inviteeName(invitees[0]),
      })
    : balanceText('common:balanceInvitesSentSuccess', {
        count: invitees.length,
      })

/**
 * Jeff, Oct 1 — the sheet's invited player slots: Add Player Slots › Reserved
 * Player Slots, the one-person invite form (⋮ Invite Player, Change Invitee),
 * Resend Invite, and the two actions that ask first — Cancel Invite and an
 * invited slot's Delete Slot, both naming who the slot is held for. BalanceTab
 * only renders what this says.
 *
 * - A form stays open on a refusal about what was typed (the modal maps it onto
 *   the field), and closes on a 409: the slot changed since it opened, nothing was
 *   saved, and the sheet is already refetching.
 * - Changing only the name of who a slot is held for sends nothing, so it says
 *   "updated" rather than "sent".
 * - A plain open slot's Delete Slot still deletes straight away — only a slot held
 *   for someone asks (`holdsInvite`).
 */
export const useSlotInviteFlows = ({
  registrations,
  mutations,
}: UseSlotInviteFlowsOptions) => {
  'use memo'
  const [form, setForm] = useState<InviteFormTarget | null>(null)
  const [confirm, setConfirm] = useState<InviteConfirmTarget | null>(null)

  const invitedSlot = (slotId: string) => {
    const target = resolveSlotTarget(registrations, slotId)

    return target && isInvitedSlot(target.slot) ? target.slot : null
  }

  const report = (error: unknown) => {
    message.error(guardMessage(error))
  }

  const write = async (
    target: InviteFormTarget,
    invitees: InviteeInput[]
  ): Promise<string> => {
    const [invitee] = invitees
    if (target.mode === 'slot') {
      await mutations.inviteIntoSlot.mutateAsync({
        slotId: target.slotId,
        invitee,
      })
      return sentToast(invitees)
    }

    await mutations.changeInvitee.mutateAsync({
      slotId: target.slotId,
      invitee,
    })
    const sameEmail =
      target.invitee.email.trim().toLowerCase() === invitee.email
    return sameEmail
      ? balanceText('common:balanceInviteeUpdatedSuccess', {
          name: inviteeName(invitee),
        })
      : sentToast(invitees)
  }

  const submitForm = async (invitees: InviteeInput[]) => {
    if (!form) {
      return
    }
    try {
      const done = await write(form, invitees)
      setForm(null)
      message.success(done)
    } catch (error) {
      if (!isConflict(error)) {
        // The modal shows it, on the field it names.
        throw error
      }
      setForm(null)
      report(error)
    }
  }

  /**
   * Add Player Slots › Reserved Player Slots: one new slot held for each player
   * (re-split when Auto is on). Resolves once the dialog may close — sent, or a
   * 409 already reported; rejects with a refusal about what was typed, for the
   * form to show on the row it names.
   */
  const addInvited = async (
    registrationId: string,
    invitees: InviteeInput[]
  ) => {
    try {
      await mutations.addInvitedSlots.mutateAsync({
        team_registration_id: registrationId,
        invitees,
      })
      message.success(sentToast(invitees))
    } catch (error) {
      if (!isConflict(error)) {
        throw error
      }
      report(error)
    }
  }

  const runConfirm = () => {
    if (!confirm) {
      return
    }
    const { kind, slotId, name } = confirm
    const mutation =
      kind === 'cancel' ? mutations.cancelInvite : mutations.removeSlot
    mutation
      .mutateAsync({ slotId })
      .then(() => {
        if (kind === 'cancel') {
          message.success(
            balanceText('common:balanceInviteCancelledSuccess', { name })
          )
        }
      })
      .catch(report)
      .finally(() => setConfirm(null))
  }

  const confirming =
    confirm !== null &&
    (confirm.kind === 'cancel'
      ? mutations.cancelInvite.isPending
      : mutations.removeSlot.isPending)

  return {
    form,
    formSubmitting:
      mutations.inviteIntoSlot.isPending || mutations.changeInvitee.isPending,
    /** An Add Player Slots › Reserved Player Slots batch is on its way. */
    adding: mutations.addInvitedSlots.isPending,
    addInvited,
    confirm,
    confirming,
    /** ⋮ Invite Player on an open slot. */
    openInvite: (slotId: string) => setForm({ mode: 'slot', slotId }),
    /** ⋮ Change Invitee, starting from who the slot is held for now. */
    openChange: (slotId: string) => {
      const invitee = invitedSlot(slotId)?.invitee
      if (!invitee) {
        return
      }
      setForm({
        mode: 'change',
        slotId,
        invitee: {
          name_first: invitee.name_first ?? '',
          name_last: invitee.name_last ?? '',
          email: invitee.email ?? '',
        },
      })
    },
    submitForm,
    closeForm: () => setForm(null),
    resend: (slotId: string) => {
      const name = inviteeName(invitedSlot(slotId)?.invitee)
      mutations.resendInvite
        .mutateAsync({ slotId })
        .then(() =>
          message.success(
            balanceText('common:balanceInviteResentSuccess', { name })
          )
        )
        .catch(report)
    },
    /** True when the slot is held for someone, so deleting it asks first. */
    holdsInvite: (slotId: string) => invitedSlot(slotId) !== null,
    requestCancel: (slotId: string) =>
      setConfirm({
        kind: 'cancel',
        slotId,
        name: inviteeName(invitedSlot(slotId)?.invitee),
      }),
    requestDelete: (slotId: string) =>
      setConfirm({
        kind: 'delete',
        slotId,
        name: inviteeName(invitedSlot(slotId)?.invitee),
      }),
    runConfirm,
    closeConfirm: () => {
      // Its write is already on its way: the dialog closes itself when that lands.
      if (confirming) {
        return
      }
      setConfirm(null)
    },
  }
}

export default useSlotInviteFlows
